"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.BaseSqlGenerator = void 0;
const ast_1 = require("../ast");
const utils_1 = require("../utils");
class BaseSqlGenerator {
    constructor(options) {
        this.options = options;
    }
    // Public entry point. Dispatches to the node-specific generator, then
    // hands aggregate output to the caller-supplied `renderAggregate` callback
    // (if any) so the caller decides how to embed the aggregate in their SQL
    // context — e.g. window-wrap for post-aggregation SELECTs, pass through
    // for GROUP BY contexts. Every recursive call from child arms goes back
    // through `generate()`, so the hook is applied once per aggregate node
    // anywhere in the tree.
    generate(node) {
        const sql = this.generateNode(node);
        if ((0, ast_1.isAggregateCall)(node) && this.options.renderAggregate) {
            return this.options.renderAggregate(sql);
        }
        return sql;
    }
    generateNode(node) {
        switch (node.type) {
            case 'BinaryOp':
                return this.generateBinaryOp(node);
            case 'UnaryOp':
                return this.generateUnaryOp(node);
            case 'If':
                return this.generateIf(node);
            case 'ConditionalAggregate':
                return this.generateConditionalAggregate(node);
            case 'CountIf':
                return this.generateCountIf(node);
            case 'ZeroArgFn':
                return this.generateZeroArgFn(node);
            case 'SingleArgFn':
                return this.generateSingleArgFn(node);
            case 'OneOrTwoArgFn':
                return this.generateOneOrTwoArgFn(node);
            case 'ZeroOrOneArgFn':
                return this.generateZeroOrOneArgFn(node);
            case 'VariadicFn':
                return this.generateVariadicFn(node);
            case 'WindowFn':
                return this.generateWindowFn(node);
            case 'ColumnRef':
                return this.generateColumnRef(node);
            case 'NumberLiteral':
                return this.generateNumberLiteral(node);
            case 'StringLiteral':
                return this.generateStringLiteral(node);
            case 'BooleanLiteral':
                return this.generateBooleanLiteral(node);
            case 'Comparison':
                return this.generateComparison(node);
            case 'Logical':
                return this.generateLogical(node);
            case 'WindowClause':
                throw new Error('WindowClause should not be generated directly');
            default:
                return (0, utils_1.assertUnreachable)(node, `Unknown node type: ${node.type}`);
        }
    }
    generateBinaryOp(node) {
        const left = this.generate(node.left);
        const right = this.generate(node.right);
        switch (node.op) {
            case '+':
            case '-':
            case '*':
                return `(${left} ${node.op} ${right})`;
            case '/':
                return `(${left} / NULLIF(${right}, 0))`;
            case '^':
                return `POWER(${left}, ${right})`;
            case '%':
                return this.generateModulo(left, right);
            default:
                return (0, utils_1.assertUnreachable)(node.op, `Unknown operator: ${node.op}`);
        }
    }
    generateModulo(left, right) {
        return `MOD(${left}, ${right})`;
    }
    generateUnaryOp(node) {
        const operand = this.generate(node.operand);
        switch (node.op) {
            case '-':
                return `(-(${operand}))`;
            case 'NOT':
                return `(NOT ${operand})`;
            default:
                return (0, utils_1.assertUnreachable)(node.op, `Unknown unary op: ${node.op}`);
        }
    }
    generateIf(node) {
        const condition = this.generate(node.condition);
        const then = this.generate(node.then);
        const else_ = node.else ? this.generate(node.else) : 'NULL';
        return `CASE WHEN ${condition} THEN ${then} ELSE ${else_} END`;
    }
    generateConditionalAggregate(node) {
        const value = this.generate(node.value);
        const condition = this.generate(node.condition);
        switch (node.name) {
            case 'SUMIF':
                return `SUM(CASE WHEN ${condition} THEN ${value} END)`;
            case 'AVERAGEIF':
                return `AVG(CASE WHEN ${condition} THEN ${value} END)`;
            default:
                return (0, utils_1.assertUnreachable)(node.name, `Unknown conditional aggregate: ${node.name}`);
        }
    }
    generateCountIf(node) {
        const condition = this.generate(node.condition);
        return `COUNT(CASE WHEN ${condition} THEN 1 END)`;
    }
    generateZeroArgFn(node) {
        switch (node.name) {
            case 'TODAY':
                return this.generateToday();
            case 'NOW':
                return this.generateNow();
            default:
                return (0, utils_1.assertUnreachable)(node.name, `Unknown zero-arg function: ${node.name}`);
        }
    }
    generateSingleArgFn(node) {
        const arg = this.generate(node.arg);
        switch (node.name) {
            case 'ABS':
                return `ABS(${arg})`;
            case 'CEIL':
            case 'CEILING':
                return `CEIL(${arg})`;
            case 'FLOOR':
                return `FLOOR(${arg})`;
            case 'LEN':
            case 'LENGTH':
                return this.generateLength(arg);
            case 'TRIM':
                return `TRIM(${arg})`;
            case 'LOWER':
                return `LOWER(${arg})`;
            case 'UPPER':
                return `UPPER(${arg})`;
            case 'YEAR':
                return this.generateExtract('YEAR', arg);
            case 'MONTH':
                return this.generateExtract('MONTH', arg);
            case 'DAY':
                return this.generateExtract('DAY', arg);
            case 'ISNULL':
                return `(${arg} IS NULL)`;
            case 'SUM':
                return `SUM(${arg})`;
            case 'AVERAGE':
            case 'AVG':
                return `AVG(${arg})`;
            default:
                return (0, utils_1.assertUnreachable)(node.name, `Unknown single-arg function: ${node.name}`);
        }
    }
    generateOneOrTwoArgFn(node) {
        const args = node.args.map((a) => this.generate(a));
        switch (node.name) {
            case 'ROUND':
                return `ROUND(${args[0]}${args[1] !== undefined ? `, ${args[1]}` : ''})`;
            case 'MIN':
                return args.length === 1
                    ? `MIN(${args[0]})`
                    : `LEAST(${args.join(', ')})`;
            case 'MAX':
                return args.length === 1
                    ? `MAX(${args[0]})`
                    : `GREATEST(${args.join(', ')})`;
            default:
                return (0, utils_1.assertUnreachable)(node.name, `Unknown one-or-two-arg function: ${node.name}`);
        }
    }
    generateZeroOrOneArgFn(node) {
        switch (node.name) {
            case 'COUNT':
                return node.arg
                    ? `COUNT(${this.generate(node.arg)})`
                    : 'COUNT(*)';
            default:
                return (0, utils_1.assertUnreachable)(node.name, `Unknown zero-or-one-arg function: ${node.name}`);
        }
    }
    generateVariadicFn(node) {
        const args = node.args.map((a) => this.generate(a));
        switch (node.name) {
            case 'CONCAT':
                return this.generateConcat(args);
            case 'COALESCE':
                return `COALESCE(${args.join(', ')})`;
            default:
                return (0, utils_1.assertUnreachable)(node.name, `Unknown variadic function: ${node.name}`);
        }
    }
    generateWindowFn(node) {
        const args = node.args.map((a) => this.generate(a));
        switch (node.name) {
            case 'ROW_NUMBER':
                return this.generateWindowFunction('ROW_NUMBER', [], node);
            case 'RANK':
                return this.generateWindowFunction('RANK', [], node);
            case 'DENSE_RANK':
                return this.generateWindowFunction('DENSE_RANK', [], node);
            case 'RUNNING_TOTAL':
                return this.generateWindowFunction('SUM', [args[0]], node, 'ROWS UNBOUNDED PRECEDING');
            case 'NTILE':
                return this.generateWindowFunction('NTILE', [args[0]], node);
            case 'FIRST':
                return this.generateWindowFunction('FIRST_VALUE', [args[0]], node);
            case 'LAST':
                return this.generateWindowFunction('LAST_VALUE', [args[0]], node, 'ROWS BETWEEN UNBOUNDED PRECEDING AND UNBOUNDED FOLLOWING');
            case 'LAG':
                return this.generateWindowFunction('LAG', args, node);
            case 'LEAD':
                return this.generateWindowFunction('LEAD', args, node);
            // TODO: unsafe cast — MOVING_SUM/MOVING_AVG assume second arg is a NumberLiteral
            // but the grammar accepts any Expression. Fix by adding a grammar rule that
            // enforces NumberLiteral in the second position (same pattern as BooleanExpression).
            case 'MOVING_SUM': {
                const preceding = node.args[1].value;
                return this.generateWindowFunction('SUM', [args[0]], node, `ROWS BETWEEN ${preceding} PRECEDING AND CURRENT ROW`);
            }
            case 'MOVING_AVG': {
                const preceding = node.args[1].value;
                return this.generateWindowFunction('AVG', [args[0]], node, `ROWS BETWEEN ${preceding} PRECEDING AND CURRENT ROW`);
            }
            default:
                return (0, utils_1.assertUnreachable)(node.name, `Unknown window function: ${node.name}`);
        }
    }
    generateColumnRef(node) {
        const column = this.options.columns[node.name];
        if (!column) {
            throw new Error(`Unknown column reference: ${node.name}. Available columns: ${Object.keys(this.options.columns).join(', ')}`);
        }
        return this.quoteIdentifier(column);
    }
    generateNumberLiteral(node) {
        return String(node.value);
    }
    generateStringLiteral(node) {
        const escaped = node.value.replace(/'/g, "''");
        return `'${escaped}'`;
    }
    generateBooleanLiteral(node) {
        return node.value ? 'TRUE' : 'FALSE';
    }
    generateComparison(node) {
        const left = this.generate(node.left);
        const right = this.generate(node.right);
        return `(${left} ${node.op} ${right})`;
    }
    generateLogical(node) {
        const left = this.generate(node.left);
        const right = this.generate(node.right);
        return `(${left} ${node.op} ${right})`;
    }
    generateConcat(args) {
        return `CONCAT(${args.join(', ')})`;
    }
    generateLength(expr) {
        return `LENGTH(${expr})`;
    }
    generateToday() {
        return 'CURRENT_DATE';
    }
    generateNow() {
        return 'CURRENT_TIMESTAMP';
    }
    generateExtract(part, expr) {
        return `EXTRACT(${part} FROM ${expr})`;
    }
    generateWindowFunction(sqlFunc, funcArgs, node, frameClause) {
        const funcCall = funcArgs.length > 0
            ? `${sqlFunc}(${funcArgs.join(', ')})`
            : `${sqlFunc}()`;
        const overParts = [];
        const wc = node.windowClause;
        if (wc?.partitionBy) {
            overParts.push(`PARTITION BY ${this.generate(wc.partitionBy)}`);
        }
        if (wc?.orderBy) {
            const dir = wc.orderBy.direction ? ` ${wc.orderBy.direction}` : '';
            overParts.push(`ORDER BY ${this.generate(wc.orderBy.column)}${dir}`);
        }
        const framePart = frameClause ? ` ${frameClause}` : '';
        return `${funcCall} OVER (${overParts.join(' ')}${framePart})`;
    }
}
exports.BaseSqlGenerator = BaseSqlGenerator;
//# sourceMappingURL=base.js.map